import { type ComponentType } from 'react';

import { type LucideIcon } from 'lucide-react';

import { type Features } from '@/shared/types/account';
import { type Desk } from '@/shared/types/desk';
import { type actions, type Module } from '@/shared/types/permissions';

export type PageGroup = 'main' | 'analytics' | 'deploy' | 'settings' | 'profile';

export type PageEntry = {
  id: string;
  i18nKey: string;
  path: string;
  icon: LucideIcon;
  group: PageGroup;
  /** If true, path is absolute (not relative to /:slug) */
  fullPath?: boolean;
  /** i18n key for the parent section, rendered as "Parent > Label" */
  parentI18nKey?: string;
  /** If set, entry is hidden unless the user has this permission (on any module, if several) */
  requiredPermission?: { module: Module | readonly Module[]; action: actions };
  /** If set, entry is hidden unless this feature flag is on */
  featureFlag?: keyof Features;
  /** Hidden when none of the pages that name it as their parent is visible. */
  hideWithoutChildren?: boolean;
};

export const ENTITY_TYPES = [
  'ai_agent',
  'knowledge_base',
  'campaign',
  'conversation',
  'environment',
  'integration',
  'rule',
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

export type EntityResult = {
  id: string;
  name: string;
  type: EntityType;
  path: string;
  icon: LucideIcon | ComponentType<{ size?: number }>;
  subtitle?: string;
  score: number;
};

export type PageDrillDown = 'ai_agent' | 'knowledge_base';

/** Desk selection with minimal required fields */
export type SelectedDesk = Pick<Desk, 'desk_id' | 'name' | 'is_service_desk'>;

export type DrillDown =
  | { kind: 'none' }
  | { kind: 'agent'; brain_id: string; name: string }
  | { kind: 'desk'; desk: SelectedDesk }
  | { kind: 'page'; type: PageDrillDown }
  | { kind: 'conversation' };

/** Desk selection with minimal required fields for agent drill-down */
export type SelectedAgent = { brain_id: string; name: string };

export type RecentPage = { path: string; label: string; timestamp: number };

/** Integration search item combining integration data with desk context */
export type IntegrationSearchItem = {
  integration: { integration_id: string; type: string };
  desk: Desk;
  label: string;
  type: string;
};
